import React, { useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Frown, Smile } from "lucide-react";

/**
 * Massage Preference Card — Step 2 of the front-desk check-in wizard
 * for guests booked strictly for one massage session today. Replaces
 * the classic Medical Questionnaire so the therapist gets pressure,
 * mood, focus/avoid areas and health flags exactly as the guest
 * intends them.
 */

export const MASSAGE_BODY_AREAS = [
  "Head",
  "Neck",
  "Shoulders",
  "Upper Back",
  "Lower Back",
  "Chest",
  "Abdomen",
  "Arms",
  "Hands",
  "Hips",
  "Glutes",
  "Thighs",
  "Calves",
  "Feet",
  "Face",
  "Scalp",
];

export const MASSAGE_HEALTH_ITEMS = [
  { key: "heart_conditions", label: "Heart Conditions" },
  { key: "muscular_pain", label: "Muscular Pain" },
  { key: "joint_problem", label: "Joint Problem" },
  { key: "blood_pressure", label: "High/Low Blood Pressure" },
  { key: "pregnant", label: "Are you Pregnant" },
  { key: "asthma", label: "Asthma" },
  { key: "claustrophobia", label: "Claustrophobia" },
  { key: "recent_injury", label: "Recent Injury/accident" },
  { key: "allergies", label: "Allergies (scent, nuts, etc)" },
  { key: "recent_surgery", label: "Recent Surgery" },
  { key: "other", label: "Other" },
];

export const SPA_TREATMENT_RELEASE = [
  "I fully understand that any Spa treatment I receive is at my own risk and are only for the purpose of relaxation, not for the purpose of medical treatment, I am aware that it is advisable to seek my doctor’s advice or obtain medical assessment before the commencement of the Spa treatment.",
  "I also acknowledge and confirm that the information provided herein is to be used as guideline for the Spa treatment and that I will inform the therapist to adjust the treatment to my comfort level if necessary.",
  "To the full extent permitted by law, I agree to release, indemnify and hold Cocoon Medical Spa and its staff harmless from all liability, actions, debts, claims and demands of any kind incurred whether arising under law or statute and whether arising directly or indirectly from the Spa treatment or the use of any products associated with the Spa treatment including without limitation, all liability for death or personal injury, loss or damage to any property and all liability for indirect or consequential loss or damage including with limitation, economic loss.",
];

const PRESSURE_OPTIONS = [
  { key: "deep", label: "Deep" },
  { key: "medium", label: "Medium" },
  { key: "light", label: "Light" },
];

function BodyAreaGrid({ selected, onToggle, testidPrefix }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-2">
      {MASSAGE_BODY_AREAS.map((area) => {
        const active = selected.includes(area);
        const testKey = area.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
        return (
          <button
            type="button"
            key={area}
            onClick={() => onToggle(area)}
            aria-pressed={active}
            data-testid={`${testidPrefix}-${testKey}`}
            className={
              "h-9 rounded-full text-xs font-medium transition-all active:scale-[0.98] px-2 " +
              (active
                ? "bg-primary text-primary-foreground shadow-sm"
                : "bg-white border border-border text-foreground hover:bg-muted/60")
            }
          >
            {area}
          </button>
        );
      })}
    </div>
  );
}

export default function MassagePreferenceStep({ value, onChange }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });

  const toggleList = (key, item) => {
    const cur = new Set(v[key] || []);
    if (cur.has(item)) cur.delete(item); else cur.add(item);
    set({ [key]: [...cur] });
  };

  // Every health question starts answered "No" so the guest only taps
  // the ones that actually apply.
  useEffect(() => {
    const hc = v.health_conditions || {};
    const missing = MASSAGE_HEALTH_ITEMS.filter((q) => !(hc[q.key] || {}).answer);
    if (!missing.length) return;
    const next = { ...hc };
    missing.forEach((q) => { next[q.key] = { ...(hc[q.key] || {}), answer: "no" }; });
    onChange({ ...v, health_conditions: next });
  }, [v, onChange]);

  const pressure = v.pressure || "";
  const moodP = typeof v.mood_physically === "number" ? v.mood_physically : 5;
  const moodE = typeof v.mood_emotionally === "number" ? v.mood_emotionally : 5;

  return (
    <div className="space-y-6" data-testid="checkin-step-2-massage">
      <div
        className="rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-teal-900"
        data-testid="checkin-massage-card-header"
      >
        <p className="text-sm font-medium">Tell us how you'd like your massage.</p>
        <p className="text-xs">Your therapist reads this before you walk in.</p>
      </div>

      {/* Pressure --------------------------------------------------- */}
      <div>
        <Label>Pressure <span className="text-rose-500">*</span></Label>
        <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="massage-pressure-group">
          {PRESSURE_OPTIONS.map((p) => {
            const active = pressure === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => set({ pressure: p.key })}
                aria-pressed={active}
                data-testid={`massage-pressure-${p.key}`}
                className={
                  "h-11 rounded-full text-sm font-medium transition-all active:scale-[0.98] " +
                  (active
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-white border border-border text-foreground hover:bg-muted/60")
                }
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Mood sliders ---------------------------------------------- */}
      <div className="space-y-4">
        <div>
          <Label data-testid="massage-mood-question">How is your feeling today?</Label>
          <p className="text-xs text-muted-foreground">Slide from 1 (low) to 10 (great).</p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Physically</Label>
            <span className="text-xs tabular-nums text-muted-foreground" data-testid="massage-mood-physically-value">{moodP} / 10</span>
          </div>
          <div className="flex items-center gap-3">
            <Frown className="w-4 h-4 text-muted-foreground shrink-0" />
            <Slider
              value={[moodP]}
              min={1}
              max={10}
              step={1}
              onValueChange={(vals) => set({ mood_physically: vals[0] })}
              data-testid="massage-mood-physically"
            />
            <Smile className="w-4 h-4 text-muted-foreground shrink-0" />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Emotionally</Label>
            <span className="text-xs tabular-nums text-muted-foreground" data-testid="massage-mood-emotionally-value">{moodE} / 10</span>
          </div>
          <div className="flex items-center gap-3">
            <Frown className="w-4 h-4 text-muted-foreground shrink-0" />
            <Slider
              value={[moodE]}
              min={1}
              max={10}
              step={1}
              onValueChange={(vals) => set({ mood_emotionally: vals[0] })}
              data-testid="massage-mood-emotionally"
            />
            <Smile className="w-4 h-4 text-muted-foreground shrink-0" />
          </div>
        </div>
      </div>

      {/* Avoid areas ----------------------------------------------- */}
      <div>
        <Label>Areas to avoid</Label>
        <p className="text-xs text-muted-foreground">Tap every area you'd rather your therapist skip.</p>
        <BodyAreaGrid
          selected={v.avoid_areas || []}
          onToggle={(a) => toggleList("avoid_areas", a)}
          testidPrefix="massage-avoid"
        />
      </div>

      {/* Focus areas ----------------------------------------------- */}
      <div>
        <Label>Areas to focus on</Label>
        <p className="text-xs text-muted-foreground">Tap the areas that need extra attention.</p>
        <BodyAreaGrid
          selected={v.focus_areas || []}
          onToggle={(a) => toggleList("focus_areas", a)}
          testidPrefix="massage-focus"
        />
      </div>

      {/* Health conditions ----------------------------------------- */}
      <div>
        <Label>Do you have any of these health conditions? <span className="text-rose-500">*</span></Label>
        <p className="text-xs text-muted-foreground mb-2">Toggle each one — if "Yes", tell your therapist which side / how long / any triggers.</p>
        <div className="rounded-md border border-border overflow-hidden" data-testid="massage-health-grid">
          {MASSAGE_HEALTH_ITEMS.map((q, i) => {
            const val = (v.health_conditions || {})[q.key] || {};
            const setAns = (ans) => set({
              health_conditions: { ...(v.health_conditions || {}), [q.key]: { ...(val || {}), answer: ans } },
            });
            const setNote = (note) => set({
              health_conditions: { ...(v.health_conditions || {}), [q.key]: { ...(val || {}), note } },
            });
            return (
              <div
                key={q.key}
                className={`grid grid-cols-[1fr_auto] gap-2 px-3 py-2 text-sm ${i > 0 ? "border-t border-border/50" : ""}`}
                data-testid={`massage-health-${q.key}`}
              >
                <span className="text-xs sm:text-sm leading-snug">{q.label} <span className="text-rose-500">*</span></span>
                <RadioGroup value={val.answer || "no"} onValueChange={setAns} className="flex gap-3">
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <RadioGroupItem value="yes" data-testid={`massage-health-${q.key}-yes`} /> Yes
                  </label>
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <RadioGroupItem value="no" data-testid={`massage-health-${q.key}-no`} /> No
                  </label>
                </RadioGroup>
                {val.answer === "yes" && (
                  <Input
                    value={val.note || ""}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Tell us the detail (side / severity / triggers)… *"
                    className="col-span-2 h-8 mt-1"
                    data-testid={`massage-health-${q.key}-note`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legal disclaimer ------------------------------------------ */}
      <div className="rounded-lg border border-border bg-secondary/40 p-3" data-testid="massage-disclaimer">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
          Spa Treatment Release <span className="text-rose-500">*</span>
        </p>
        <div className="max-h-40 overflow-y-auto text-[12px] leading-relaxed space-y-2 pr-1" data-testid="massage-disclaimer-text">
          {SPA_TREATMENT_RELEASE.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
        <label className="flex items-start gap-2 cursor-pointer select-none mt-3">
          <Checkbox
            checked={!!v.agreed_disclaimer}
            onCheckedChange={(c) => set({ agreed_disclaimer: !!c })}
            className="mt-0.5"
            data-testid="massage-disclaimer-agree"
          />
          <span className="text-sm">I have read and agree to the Spa Treatment Release above.</span>
        </label>
      </div>
    </div>
  );
}
